import { FieldNode, FieldNodeToReference } from '@modusign/react-signing/types';
import { createAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { getSignatureAbsoluteViewport } from '@app/libs/fieldViewport';
import { scaleSelector } from '@app/libs/reactPdfJs/store/selectors';
import {
  CONTRACT_PROGRESS_ADDRESS_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_CHECKBOX_FIELD_CHECKED_CHANGE,
  CONTRACT_PROGRESS_CHECKBOX_GROUP_CHECKED_COUNT_CHANGE,
  CONTRACT_PROGRESS_COMPANY_NAME_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_COMPLETION_FAILURE,
  CONTRACT_PROGRESS_COMPLETION_REQUEST,
  CONTRACT_PROGRESS_COMPLETION_SUCCESS,
  CONTRACT_PROGRESS_CONTRACT_LOAD_FAILURE,
  CONTRACT_PROGRESS_CONTRACT_LOAD_REQUEST,
  CONTRACT_PROGRESS_CONTRACT_LOAD_SUCCESS,
  CONTRACT_PROGRESS_CREDENTIALS_UPDATE,
  CONTRACT_PROGRESS_DATE_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_DROPDOWN_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_FIELD_FIRST_FOCUS,
  CONTRACT_PROGRESS_FIELD_FOCUSED,
  CONTRACT_PROGRESS_FIELD_POSITION_CHANGE,
  CONTRACT_PROGRESS_FIELD_POSITION_INITIALIZE,
  CONTRACT_PROGRESS_FIELD_SIGNATURE_SIZE_CHANGE,
  CONTRACT_PROGRESS_FIELD_SIZE_CHANGE,
  CONTRACT_PROGRESS_NAME_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_FAILURE,
  CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_REQUEST,
  CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_SUCCESS,
  CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_FAILURE,
  CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_REQUEST,
  CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_SUCCESS,
  CONTRACT_PROGRESS_REJECTION_FAILURE,
  CONTRACT_PROGRESS_REJECTION_REQUEST,
  CONTRACT_PROGRESS_REJECTION_SUCCESS,
  CONTRACT_PROGRESS_SEQUENCE_INSERT,
  CONTRACT_PROGRESS_SEQUENCE_REMOVE,
  CONTRACT_PROGRESS_SIGNATURE_INSERT,
  CONTRACT_PROGRESS_SIGNATURE_INSERTION_RESET,
  CONTRACT_PROGRESS_SIGNATURE_SELECT,
  CONTRACT_PROGRESS_TAB_KEY_COUNT,
  CONTRACT_PROGRESS_TERMS_URL_LOAD_FAILURE,
  CONTRACT_PROGRESS_TERMS_URL_LOAD_REQUEST,
  CONTRACT_PROGRESS_TERMS_URL_LOAD_SUCCESS,
  CONTRACT_PROGRESS_TEXT_FIELD_FONT_SIZE_CHANGE,
  CONTRACT_PROGRESS_TEXT_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_TRACKER_NEXT_SEQUENCE,
  CONTRACT_PROGRESS_TRACKER_WITHIN_BOUNDARIES,
} from '@app/store/actionTypes';
import { ContractProgressAttachmentsAction } from '@app/store/contract-progress/attachments/actions';
import { getSignatureFieldIds } from '@app/store/contract-progress/selectors';
import SignatureFieldType from '@app/store/contract-progress/SignatureFieldType';
import SigningMethodType from '@app/store/contract-progress/SigningMethodType';
import UserProvider from '@app/store/contract-progress/UserProvider';
import { Thunk } from '@app/store/types';

import ContractProgressStep from './ContractProgressStep';

export function contractProgressCheckboxFieldCheckedChange(id: string, checked: boolean) {
  return {
    type: CONTRACT_PROGRESS_CHECKBOX_FIELD_CHECKED_CHANGE,
    payload: { id, checked },
  };
}

export const contractProgressCheckboxGroupCheckedCountChange = createAction(
  CONTRACT_PROGRESS_CHECKBOX_GROUP_CHECKED_COUNT_CHANGE,
  (payload: { id: string; required: boolean; count: number }) => {
    return {
      payload,
    };
  }
);

export function contractProgressCompletionRequest(payload?: {
  contractLinkId?: string;
  exitPath?: string;
}) {
  return {
    type: CONTRACT_PROGRESS_COMPLETION_REQUEST,
    payload,
  };
}

export const contractProgressCompletionSuccess = createAction<
  {
    documentId: string;
    participantId: string;
  },
  typeof CONTRACT_PROGRESS_COMPLETION_SUCCESS
>(CONTRACT_PROGRESS_COMPLETION_SUCCESS);

export function contractProgressCompletionFailure() {
  return {
    type: CONTRACT_PROGRESS_COMPLETION_FAILURE,
  };
}

export function contractProgressRejectionRequest(payload: {
  documentId: string;
  participantId: string;
  message: string;
  isDocumentBlock: boolean;
  exitPath?: string;
}) {
  return {
    type: CONTRACT_PROGRESS_REJECTION_REQUEST,
    payload,
  };
}

export const contractProgressRejectionSuccess = createAction(CONTRACT_PROGRESS_REJECTION_SUCCESS);

export function contractProgressRejectionFailure() {
  return {
    type: CONTRACT_PROGRESS_REJECTION_FAILURE,
  };
}

export function contractProgressSignatureInsertionReset(
  initialTrackedId: string,
  signatureIds: string[]
) {
  return {
    type: CONTRACT_PROGRESS_SIGNATURE_INSERTION_RESET,
    payload: { initialTrackedId, signatureIds },
  };
}

export function contractProgressFieldFirstFocus() {
  return {
    type: CONTRACT_PROGRESS_FIELD_FIRST_FOCUS,
  };
}

export function contractProgressRequiredFieldFocused(payload: string) {
  return {
    type: CONTRACT_PROGRESS_FIELD_FOCUSED,
    payload,
  };
}

export function contractProgressFieldPositionChange(
  id: string,
  currentRelativeOffsetX: number,
  currentRelativeOffsetY: number,
  currentRelativePageX: number,
  currentRelativePageY: number
) {
  return {
    type: CONTRACT_PROGRESS_FIELD_POSITION_CHANGE,
    payload: {
      currentRelativeOffsetX,
      currentRelativeOffsetY,
      currentRelativePageX,
      currentRelativePageY,
      id,
    },
  };
}

export function contractProgressFieldPositionInitialize(
  id: string,
  relativePageX: number,
  relativePageY: number
) {
  return {
    type: CONTRACT_PROGRESS_FIELD_POSITION_INITIALIZE,
    payload: {
      id,
      relativePageX,
      relativePageY,
    },
  };
}

export function contractProgressFieldSizeChange(
  id: string,
  absoluteHeight: number,
  absoluteWidth: number,
  relativeHeight: number,
  relativeWidth: number,
  linkedFieldIds: string[] = []
) {
  return {
    type: CONTRACT_PROGRESS_FIELD_SIZE_CHANGE,
    payload: {
      absoluteHeight,
      absoluteWidth,
      id,
      linkedFieldIds,
      relativeHeight,
      relativeWidth,
    },
  };
}

export function contractProgressSignatureInsert(payload: string) {
  return {
    type: CONTRACT_PROGRESS_SIGNATURE_INSERT,
    payload,
  };
}

export function contractProgressSignatureSelect(
  id: string,
  s3Key: string,
  type: SignatureFieldType,
  url: string,
  inputId: string,
  signatureFieldIds: string[],
  scale = 1
) {
  const isStamp = type.indexOf('도장') !== -1;
  const absoluteViewport = getSignatureAbsoluteViewport(window.innerWidth, isStamp);
  const { height, width } = absoluteViewport;

  const absoluteHeight = height * scale;
  const absoluteWidth = width * scale;
  const relativeHeight = (absoluteHeight / 868) * scale;
  const relativeWidth = (absoluteWidth / 868) * scale;

  return {
    type: CONTRACT_PROGRESS_SIGNATURE_SELECT,
    payload: {
      absoluteHeight,
      absoluteWidth,
      id,
      inputId,
      s3Key,
      type,
      url,
      relativeHeight,
      relativeWidth,
      signatureFieldIds,
    },
  };
}

export function contractProgressFieldSignatureSizeChange(
  absoluteHeight: number,
  absoluteWidth: number,
  signatureIds: string[]
) {
  return {
    type: CONTRACT_PROGRESS_FIELD_SIGNATURE_SIZE_CHANGE,
    payload: { absoluteHeight, absoluteWidth, signatureIds },
  };
}

export const contractProgressStepChange = createAction<ContractProgressStep>(
  'contractProgress/contractProgressStepChange'
);

export const forwardContractProgressStep = createAction(
  'contractProgress/forwardContractProgressStep'
);

export const backwardContractProgressStep = createAction(
  'contractProgress/backwardContractProgressStep'
);

export const contractProgressInsertSequence = createAction(
  CONTRACT_PROGRESS_SEQUENCE_INSERT,
  (payload: { fieldNode: Exclude<FieldNode, { type: 'AUTOFILL_GROUP' }> }) => {
    return {
      payload,
    };
  }
);

export const contractProgressRemoveSequence = createAction(
  CONTRACT_PROGRESS_SEQUENCE_REMOVE,
  (payload: { id: string }) => ({ payload })
);

export function contractProgressTrackerNextSequence(payload: string) {
  return {
    type: CONTRACT_PROGRESS_TRACKER_NEXT_SEQUENCE,
    payload,
  };
}

export function contractProgressTrackerWithinBoundaries(payload: boolean) {
  return {
    type: CONTRACT_PROGRESS_TRACKER_WITHIN_BOUNDARIES,
    payload,
  };
}

export function contractProgressContractLoadRequest(payload: {
  authenticationKey: string;
  documentId: string;
  contractLinkId?: string;
  participantId: string;
}) {
  return {
    type: CONTRACT_PROGRESS_CONTRACT_LOAD_REQUEST,
    payload,
  };
}

export const contractProgressContractLoadSuccess = createAction(
  CONTRACT_PROGRESS_CONTRACT_LOAD_SUCCESS,
  (payload: {
    contractLinkId?: string;
    file: string;
    hasSignerAttachment: boolean;
    hasAuthenticationRequest: boolean;
    isLastOrder: boolean;
    isLastInPersonSigner: boolean;
    pdfArrayBuffer: ArrayBuffer;
    isReSigner: boolean;
    fields: FieldNodeToReference[];
  }) => ({ payload })
);

export const initializeContractProgress = createAction('contract-progress/initialize');

export function contractProgressContractLoadFailure() {
  return {
    type: CONTRACT_PROGRESS_CONTRACT_LOAD_FAILURE,
  };
}

export function updateCredentials(payload: {
  authenticationKey: string;
  documentId: string;
  participantId: string;
}) {
  return {
    type: CONTRACT_PROGRESS_CREDENTIALS_UPDATE,
    payload,
  };
}

export function contractProgressTermsUrlLoadRequest() {
  return {
    type: CONTRACT_PROGRESS_TERMS_URL_LOAD_REQUEST,
  };
}

export function contractProgressTermsUrlLoadSuccess(payload: {
  privacyPolicyURL: string | null;
  termOfUseURL: string;
}) {
  return {
    type: CONTRACT_PROGRESS_TERMS_URL_LOAD_SUCCESS,
    payload,
  };
}

export function contractProgressTermsUrlLoadFailure() {
  return {
    type: CONTRACT_PROGRESS_TERMS_URL_LOAD_FAILURE,
  };
}

export function contractProgressTextFieldFontSizeChange(id: string, fontSize: number) {
  return {
    type: CONTRACT_PROGRESS_TEXT_FIELD_FONT_SIZE_CHANGE,
    payload: {
      fontSize,
      id,
    },
  };
}

export function contractProgressTextFieldValueChange(
  id: string,
  value: string,
  required: boolean,
  isValid: boolean
) {
  return {
    type: CONTRACT_PROGRESS_TEXT_FIELD_VALUE_CHANGE,
    payload: {
      id,
      required,
      value,
      isValid,
    },
  };
}

export function contractProgressDropdownFieldValueChange(
  id: string,
  value: string,
  required: boolean
) {
  return {
    type: CONTRACT_PROGRESS_DROPDOWN_FIELD_VALUE_CHANGE,
    payload: {
      id,
      required,
      value,
    },
  };
}

export function contractProgressDateFieldValueChange(id: string, value: string, required: boolean) {
  return {
    type: CONTRACT_PROGRESS_DATE_FIELD_VALUE_CHANGE,
    payload: {
      id,
      required,
      value,
    },
  };
}

export function contractProgressAddressFieldValueChange(
  fields: { id: string; required: boolean }[],
  value: string
) {
  return {
    type: CONTRACT_PROGRESS_ADDRESS_FIELD_VALUE_CHANGE,
    payload: {
      fields,
      value,
    },
  };
}

export function contractProgressNameFieldValueChange(
  fields: { id: string; required: boolean }[],
  value: string
) {
  return {
    type: CONTRACT_PROGRESS_NAME_FIELD_VALUE_CHANGE,
    payload: {
      fields,
      value,
    },
  };
}

export function contractProgressCompanyNameFieldValueChange(
  fields: { id: string; required: boolean }[],
  value: string
) {
  return {
    type: CONTRACT_PROGRESS_COMPANY_NAME_FIELD_VALUE_CHANGE,
    payload: {
      fields,
      value,
    },
  };
}

/**
 * 예전에 서명 링크 주소가 /editor/validation/:token 였는데 변경되서
 * 하위 호환을 위해 token 기준으로 링크 정보 조회 후 /participant에
 * di, pi, lk, smt query string 붙여서 리다이렉트 하기 위한 액션입니다.
 * @see: https://modusign.slack.com/archives/C01PP3J0U3H/p1622775710061600
 *
 * @param payload token
 */
export function contractProgressRedirectFromTokenRequest(payload: string) {
  return {
    type: CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_REQUEST,
    payload,
  };
}

export function contractProgressRedirectFromTokenSuccess(payload: {
  documentId: string;
  participantId: string;
  linkKey: string;
  signingMethodType: SigningMethodType;
}) {
  return {
    type: CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_SUCCESS,
    payload,
  };
}

export function contractProgressRedirectFromTokenFailure(payload: AxiosError) {
  return {
    type: CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_FAILURE,
    payload,
  };
}

export function loadParticipantUserType(payload: string) {
  return {
    type: CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_REQUEST,
    payload,
  };
}

export function loadParticipantUserTypeSuccess(payload: UserProvider) {
  return {
    type: CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_SUCCESS,
    payload,
  };
}

export function loadParticipantUserTypeFailure(payload: AxiosError) {
  return {
    type: CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_FAILURE,
    payload,
  };
}

export function contractProgressTabKeyCount() {
  return {
    type: CONTRACT_PROGRESS_TAB_KEY_COUNT,
  };
}

export type ContractProgressAction =
  | ContractProgressAttachmentsAction
  | ReturnType<typeof contractProgressCheckboxFieldCheckedChange>
  | ReturnType<typeof contractProgressCheckboxGroupCheckedCountChange>
  | ReturnType<typeof contractProgressCompletionRequest>
  | ReturnType<typeof contractProgressCompletionSuccess>
  | ReturnType<typeof contractProgressCompletionFailure>
  | ReturnType<typeof contractProgressRejectionRequest>
  | ReturnType<typeof contractProgressRejectionSuccess>
  | ReturnType<typeof initializeContractProgress>
  | ReturnType<typeof contractProgressRejectionFailure>
  | ReturnType<typeof contractProgressSignatureInsertionReset>
  | ReturnType<typeof contractProgressFieldFirstFocus>
  | ReturnType<typeof contractProgressRequiredFieldFocused>
  | ReturnType<typeof contractProgressFieldPositionChange>
  | ReturnType<typeof contractProgressFieldPositionInitialize>
  | ReturnType<typeof contractProgressFieldSizeChange>
  | ReturnType<typeof contractProgressSignatureInsert>
  | ReturnType<typeof contractProgressSignatureSelect>
  | ReturnType<typeof contractProgressFieldSignatureSizeChange>
  | ReturnType<typeof contractProgressInsertSequence>
  | ReturnType<typeof contractProgressRemoveSequence>
  | ReturnType<typeof contractProgressTrackerNextSequence>
  | ReturnType<typeof contractProgressTrackerNextSequence>
  | ReturnType<typeof contractProgressTrackerWithinBoundaries>
  | ReturnType<typeof contractProgressContractLoadRequest>
  | ReturnType<typeof contractProgressContractLoadSuccess>
  | ReturnType<typeof contractProgressContractLoadFailure>
  | ReturnType<typeof updateCredentials>
  | ReturnType<typeof contractProgressTermsUrlLoadRequest>
  | ReturnType<typeof contractProgressTermsUrlLoadSuccess>
  | ReturnType<typeof contractProgressTermsUrlLoadFailure>
  | ReturnType<typeof contractProgressTextFieldFontSizeChange>
  | ReturnType<typeof contractProgressTextFieldValueChange>
  | ReturnType<typeof contractProgressDropdownFieldValueChange>
  | ReturnType<typeof contractProgressDateFieldValueChange>
  | ReturnType<typeof contractProgressAddressFieldValueChange>
  | ReturnType<typeof contractProgressNameFieldValueChange>
  | ReturnType<typeof contractProgressCompanyNameFieldValueChange>
  | ReturnType<typeof contractProgressRedirectFromTokenRequest>
  | ReturnType<typeof contractProgressRedirectFromTokenSuccess>
  | ReturnType<typeof contractProgressRedirectFromTokenFailure>
  | ReturnType<typeof loadParticipantUserType>
  | ReturnType<typeof loadParticipantUserTypeSuccess>
  | ReturnType<typeof loadParticipantUserTypeFailure>
  | ReturnType<typeof contractProgressTabKeyCount>;

/**
 * 사인, 도장 모달로부터 서명을 선택한다.
 */
export const selectProgressSignature =
  (id: string, s3Key: string, type: SignatureFieldType, url: string, fieldId: string): Thunk =>
  (dispatch, getState) => {
    const signatureFieldIds: string[] = getSignatureFieldIds(getState());
    const scale: number = scaleSelector(getState());
    dispatch(
      contractProgressSignatureSelect(id, s3Key, type, url, fieldId, signatureFieldIds, scale)
    );
  };

export const changeSignatureFieldSize =
  (absoluteHeight: number, absoluteWidth: number): Thunk =>
  (dispatch, getState) => {
    const signatureFieldIds: string[] = getSignatureFieldIds(getState());
    dispatch(
      contractProgressFieldSignatureSizeChange(absoluteHeight, absoluteWidth, signatureFieldIds)
    );
  };
